import {
  ArrowUpDown,
  BrainCircuit,
  GitMerge,
  Pencil,
  Trash,
} from "lucide-react";
import {
  ChangeEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaCheck, FaFolder } from "react-icons/fa";
import { formatFileSize, getFileIcon } from "utils";
import { FileSystemItem } from "types";
import TooltipHorizon from "components/tooltip";
import dayjs from "dayjs";
import { DATE_TIME_FORMAT } from "constants/date";
import DocumentStatusBadge from "components/dataDisplay/DocumentStatusBadge";
import { useTranslation } from "react-i18next";

interface FolderListViewProps {
  fileItems: FileSystemItem[];
  isDocumentFileType?: boolean;
  onSort?: (by: "name" | "size") => void;
  handleDelete?: (item?: FileSystemItem) => void;
  onFileClick?: (item: FileSystemItem) => void;
  setSelectedFiles?: (item: FileSystemItem[]) => void;
  selectedFiles?: FileSystemItem[];
  handleEdit?: (itemId: string, value: string) => Promise<void>;
  handleDeepExtract?: boolean;
  willUpdateUrl?: boolean;
  handleNavigate?: (item: FileSystemItem) => void;
  isFolderView?: boolean;
  onBack?: () => void;
  willShowCheckbox?: (item: FileSystemItem) => boolean;
  otherActions?: {
    label: string;
    icon: ReactNode;
    onClick: (item: FileSystemItem) => void;
  }[];
  onDeleteWorkflow?: (id: string) => void;
}

export const FolderListView = ({
  fileItems,
  onSort,
  handleDelete,
  onFileClick,
  setSelectedFiles,
  handleEdit,
  handleDeepExtract,
  selectedFiles = [],
  willUpdateUrl = true,
  isFolderView = false,
  handleNavigate,
  onBack,
  willShowCheckbox,
  otherActions,
  onDeleteWorkflow,
  isDocumentFileType = false,
}: FolderListViewProps) => {
  const { t } = useTranslation();
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState("");
  const editInputRef = useRef(null);
  const showCheckbox = (item: FileSystemItem) => {
    if (willShowCheckbox) return willShowCheckbox(item);
    return true;
  };

  const isShowStatusColumn = useMemo(() => {
    return (
      isDocumentFileType && fileItems.some((item) => item.type === "Document")
    );
  }, [isDocumentFileType, fileItems]);

  const handleCheckboxClick = (
    e: ChangeEvent<HTMLInputElement>,
    item: FileSystemItem
  ) => {
    e.stopPropagation();
    if (onFileClick) {
      onFileClick(item);
    }
  };

  const handleEditRow = async (item: FileSystemItem, newName: string) => {
    if (!newName.trim() || item.name === newName) return;

    await handleEdit(item.id, newName);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedFiles.length === 0) return;

      if (e.key === "Delete" && handleDelete) {
        e.preventDefault();
        handleDelete();
      } else if (e.key === "F2" && handleEdit) {
        e.preventDefault();
        if (selectedFiles.length === 1) {
          const item = fileItems.find(
            (item) => item.id === selectedFiles[0].id
          );
          if (item) {
            setEditingItem(item.id);
            setEditValue(item.name);
          }
        }
      } else if (e.key === "Enter" && willUpdateUrl) {
        e.preventDefault();
        if (selectedFiles.length === 1) {
          const item = fileItems.find(
            (item) => item.id === selectedFiles[0].id
          );
          if (item) {
            handleNavigate(item);
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedFiles,
    fileItems,
    handleDelete,
    handleEdit,
    handleNavigate,
    willUpdateUrl,
  ]);

  useEffect(() => {
    if (editingItem && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingItem]);

  const isFileSelected = (item: FileSystemItem) =>
    (isFolderView || item.type !== "Folder") &&
    selectedFiles.some((f) => f.id === item.id);
  const isFolderSelected = (item: FileSystemItem) =>
    !onFileClick && selectedFiles.some((f) => f.id === item.id);
  return (
    <div className="overflow-auto">
      <table className="w-full select-none">
        <thead className="border-b bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
          <tr>
            {(isFolderView || onFileClick) && (
              <th className="w-12 p-4">
                <label className="flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    aria-label={t("common.selectAll")}
                    checked={(() => {
                      const selectableItems = fileItems.filter(
                        (f) =>
                          showCheckbox(f) &&
                          (isFolderView || f.type !== "Folder")
                      );
                      if (selectableItems.length === 0) return false;

                      const allSelected = selectableItems.every((item) =>
                        selectedFiles.some((f) => f.id === item.id)
                      );
                      return allSelected;
                    })()}
                    onChange={(e) => {
                      const selectableItems = fileItems.filter(
                        (f) =>
                          showCheckbox(f) &&
                          (isFolderView || f.type !== "Folder")
                      );
                      if (e.target.checked) {
                        setSelectedFiles?.(selectableItems);
                      } else {
                        setSelectedFiles?.([]);
                      }
                    }}
                    className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-blue-600 dark:border-gray-600 dark:bg-gray-900"
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          fileItems.length > 0 &&
                          fileItems.some((item) =>
                            selectedFiles.some((f) => f.id === item.id)
                          ) &&
                          !fileItems.every((item) =>
                            selectedFiles.some((f) => f.id === item.id)
                          );
                      }
                    }}
                  />
                  <span className="sr-only">Select all</span>
                </label>
              </th>
            )}
            <th className="p-4 text-left font-medium text-gray-900 dark:text-gray-100">
              {onSort ? (
                <button
                  onClick={() => onSort("name")}
                  className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  <span>{t("common.name")}</span>
                  <ArrowUpDown className="h-4 w-4" />
                </button>
              ) : (
                <span>{t("common.name")}</span>
              )}
            </th>
            {fileItems[0]?.type && (
              <th className="p-4 text-left font-medium text-gray-900 dark:text-gray-100">
                {onSort ? (
                  <button
                    onClick={() => onSort("size")}
                    className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    <span>{t("common.size")}</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </button>
                ) : (
                  <span>{t("common.size")}</span>
                )}
              </th>
            )}
            {fileItems[0]?.type && (
              <th className="p-4 text-left font-medium text-gray-900 dark:text-gray-100">
                {t("common.type")}
              </th>
            )}
            {isShowStatusColumn && (
            <th className="p-4 text-left font-medium text-gray-900 dark:text-gray-100">
              {t("common.status")}
            </th>
            )}
            <th className=" p-4 text-left font-medium text-gray-900 dark:text-gray-100">
              {t("common.createdAt")}
            </th>

            {(handleDelete || handleDelete || otherActions) && (
              <th className="w-0 max-w-[10px]   text-left font-medium text-gray-900 dark:text-gray-100"></th>
            )}
          </tr>
        </thead>
        <tbody className="select-none ">
          {onBack && (
            <tr
              onClick={onBack}
              className="cursor-pointer border-b transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              {(isFolderView || onFileClick) && <td className="p-4" />}
              <td className="p-4">
                <div className="flex items-center space-x-3">
                  <FaFolder className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    ...
                  </span>
                </div>
              </td>
              {fileItems[0]?.type && <td className="p-4" />}
              {fileItems[0]?.type && <td className="p-4" />}
              <td className="p-4" />
              <td className="p-4" />
              {(handleDelete || otherActions) && <td className="p-4" />}
            </tr>
          )}
          {fileItems.map((item) => {
            const isSelected = isFileSelected(item) || isFolderSelected(item);
            const isEditing = editingItem === item.id;
            const isDocumentPdf = item?.document_metadata?.file_type === "pdf";

            return (
              <tr
                key={item.id}
                className={`border-b transition-colors ${
                  isSelected
                    ? "border-blue-200 bg-blue-50 dark:border-blue-400 dark:bg-blue-900"
                    : onFileClick || handleNavigate
                    ? "hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                    : ""
                }`}
              >
                {showCheckbox(item) && (isFolderView || onFileClick) && (
                  <td className="p-4">
                    {(isFolderView || item.type !== "Folder") && (
                      <label className={`flex  items-center justify-center`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleCheckboxClick(e, item)}
                          className="peer sr-only"
                        />
                        <span
                          className={`inline-flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
                            isSelected
                              ? "border-blue-500 bg-blue-500 dark:border-blue-400 dark:bg-blue-500"
                              : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
                          }`}
                        >
                          {isSelected ? (
                            <FaCheck
                              size={10}
                              className="text-white dark:text-gray-900"
                            />
                          ) : null}
                        </span>
                      </label>
                    )}
                  </td>
                )}
                {!showCheckbox(item) && <td className="p-4"></td>}
                <td
                  className={`${
                    onFileClick || handleNavigate ? "cursor-pointer" : ""
                  } p-4`}
                  onClick={() =>
                    handleNavigate ? handleNavigate(item) : onFileClick?.(item)
                  }
                >
                  <div className="flex items-center space-x-3">
                    {item.type === "Folder" ? (
                      <FaFolder className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                    ) : item.type === "Workflow" ? (
                      <GitMerge className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                    ) : (
                      getFileIcon(
                        item.document_metadata?.file_type || item.name
                      )
                    )}
                    <span
                      className={`max-w-[200px] truncate font-medium ${
                        isSelected
                          ? "text-blue-700 dark:text-blue-200"
                          : "text-gray-900 dark:text-gray-100"
                      }`}
                    >
                      {isEditing ? (
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editValue}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => {
                            setEditingItem(null);
                            setEditValue("");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleEditRow(item, editValue);
                            } else if (e.key === "Escape") {
                              setEditingItem(null);
                              setEditValue("");
                            }
                          }}
                          className="max-w-[200px] rounded border px-2 py-1 focus:border-blue-500 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:border-blue-400"
                        />
                      ) : (
                        <span
                          className={`max-w-[200px] truncate font-medium ${
                            isSelected
                              ? "text-blue-700 dark:text-blue-200"
                              : "text-gray-900 dark:text-gray-100"
                          }`}
                        >
                          {item.name}
                        </span>
                      )}
                    </span>
                  </div>
                </td>
                {fileItems[0]?.type && (
                  <td className="p-4 text-gray-600 dark:text-gray-300">
                    {item.type !== "Folder" &&
                      item.type !== "Workflow" &&
                      formatFileSize(item.document_metadata?.file_size ?? 0)}
                  </td>
                )}
                {item.type && (
                  <td className="whitespace-nowrap p-4 text-sm text-gray-500 dark:text-gray-400">
                    {item.type === "Folder"
                      ? t("common.folder")
                      : item.type === "Workflow"
                      ? t("common.workflow")
                      : item.document_metadata.file_type}
                  </td>
                )}
                {isShowStatusColumn && (
                  <td className="p-4">
                    {item.status || item?.document_metadata?.status ? (
                      <DocumentStatusBadge
                        status={item.status || item?.document_metadata?.status}
                      />
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">
                        -
                      </span>
                    )}
                  </td>
                )}

                <td className="max-w-[200px] truncate p-4 uppercase text-gray-600 dark:text-gray-300">
                  {dayjs(item.created_at).format(DATE_TIME_FORMAT)}
                </td>
                {(handleDelete || otherActions) && (
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      {otherActions?.map((action, index) => (
                        <TooltipHorizon
                          key={index}
                          extra=""
                          trigger={
                            <button
                              onClick={() => action.onClick(item)}
                              className="rounded p-2 hover:bg-gray-100 dark:hover:bg-navy-700"
                            >
                              {action.icon}
                            </button>
                          }
                          content={<span>{action.label}</span>}
                          placement="top"
                        />
                      ))}

                      {handleDeepExtract && handleNavigate && isDocumentPdf && (
                        <TooltipHorizon
                          extra=""
                          trigger={
                            <button
                              onClick={() => {
                                handleNavigate({
                                  ...item,
                                  type: "DeepExtract",
                                });
                              }}
                              className="rounded p-2 hover:bg-gray-100 dark:hover:bg-navy-700"
                            >
                              <BrainCircuit
                                size={15}
                                className="text-brand-500"
                              />
                            </button>
                          }
                          content={<span>DeepExtract</span>}
                          placement="top"
                        />
                      )}
                      {handleEdit && (
                        <TooltipHorizon
                          extra=""
                          trigger={
                            <button
                              onClick={() => {
                                setEditingItem(item.id);
                                setEditValue(item.name);
                              }}
                              className="rounded p-2 hover:bg-gray-100 dark:hover:bg-navy-700"
                            >
                              <Pencil size={15} className="text-brand-500" />
                            </button>
                          }
                          content={<span>{t("common.edit")}</span>}
                          placement="top"
                        />
                      )}
                      {handleDelete && (
                        <TooltipHorizon
                          extra=""
                          trigger={
                            <button
                              onClick={() => {
                                if (item.type === "Workflow") {
                                  if (onDeleteWorkflow)
                                    onDeleteWorkflow(item.id);
                                } else {
                                  handleDelete(item);
                                }
                              }}
                              className="rounded p-2 hover:bg-gray-100 dark:hover:bg-navy-700"
                            >
                              <Trash size={15} className="text-red-500" />
                            </button>
                          }
                          content={<span>{t("common.delete")}</span>}
                          placement="top"
                        />
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
